import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TicketCategory, TicketSeverity, ISystemContext, ISupportTicket } from 'src/app/models/ticket.interface';
import { SupportService } from 'src/app/services/support.service';
import { Subject, debounceTime, takeUntil } from 'rxjs';

@Component({
  selector: 'app-create-support-ticket-modal',
  templateUrl: './create-support-ticket-modal.component.html',
  styleUrls: ['./create-support-ticket-modal.component.scss'],
  standalone: false,
})
export class CreateSupportTicketModalComponent implements OnInit, OnDestroy {

  @Input() abstract: string;
  @Input() logId: string;

  // Wizard state
  currentStep: number = 1;

  // Form data
  formData = {
    category: '' as TicketCategory,
    severity: TicketSeverity.MEDIUM as TicketSeverity,
    abstract: '',
    description: '',
    reproducibilitySteps: '',
    expectedBehavior: '',
    actualBehavior: '',
    systemContext: {} as ISystemContext,
  };

  // Reference data
  categories = Object.values(TicketCategory);
  severities = Object.values(TicketSeverity);

  // File handling
  selectedFiles: File[] = [];

  // Similar tickets search
  similarTickets: ISupportTicket[] = [];
  isSearching: boolean = false;
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // UI state
  errorMessage: string = '';
  successMessage: string = '';
  isSubmitting: boolean = false;
  showOptionalFields: boolean = false;

  constructor(
    private modalController: ModalController,
    private supportService: SupportService
  ) {}

  ngOnInit() {
    // Pre-fill abstract if passed in
    if (this.abstract) {
      this.formData.abstract = this.abstract;
    }

    // Capture system context
    this.captureSystemContext();

    // Setup debounced search
    this.searchSubject.pipe(
      debounceTime(800),
      takeUntil(this.destroy$)
    ).subscribe(query => {
      this.performSearch(query);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Called when description changes - trigger search for similar tickets
   */
  onDescriptionChange(): void {
    const searchQuery = (this.formData.abstract || this.formData.description).trim();
    if (searchQuery.length >= 3) {
      this.searchSubject.next(searchQuery);
    } else {
      this.similarTickets = [];
    }
  }

  /**
   * Perform search for similar tickets
   */
  private performSearch(query: string): void {
    this.isSearching = true;
    this.similarTickets = [];

    this.supportService.searchSimilarTickets(query, 5).subscribe({
      next: (tickets) => {
        this.similarTickets = tickets;
        this.isSearching = false;
      },
      error: (err) => {
        console.error('Error searching similar tickets:', err);
        this.isSearching = false;
        // Don't show error to user, just silently fail
      },
    });
  }

  /**
   * Capture browser, OS, and device information
   */
  private captureSystemContext(): void {
    const userAgent = navigator.userAgent;

    // Parse browser
    let browser = 'Unknown';
    if (userAgent.includes('Chrome')) {
      const match = userAgent.match(/Chrome\/(\d+)/);
      browser = `Chrome ${match ? match[1] : ''}`.trim();
    } else if (userAgent.includes('Safari')) {
      const match = userAgent.match(/Version\/(\d+)/);
      browser = `Safari ${match ? match[1] : ''}`.trim();
    } else if (userAgent.includes('Firefox')) {
      const match = userAgent.match(/Firefox\/(\d+)/);
      browser = `Firefox ${match ? match[1] : ''}`.trim();
    } else if (userAgent.includes('Edge')) {
      const match = userAgent.match(/Edg\/(\d+)/);
      browser = `Edge ${match ? match[1] : ''}`.trim();
    }

    // Parse OS
    let os = 'Unknown';
    if (userAgent.includes('Windows')) {
      os = 'Windows';
    } else if (userAgent.includes('Mac OS')) {
      os = 'macOS';
    } else if (userAgent.includes('Linux')) {
      os = 'Linux';
    } else if (userAgent.includes('Android')) {
      os = 'Android';
    } else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
      os = 'iOS';
    }

    // Detect device type
    let deviceType = 'desktop';
    if (window.innerWidth < 480) {
      deviceType = 'mobile';
    } else if (window.innerWidth < 1024) {
      deviceType = 'tablet';
    }

    this.formData.systemContext = {
      userAgent,
      browser,
      os,
      deviceType,
      timestamp: new Date(),
    };
  }

  /**
   * Check if current step is valid to proceed
   */
  isStepValid(): boolean {
    switch (this.currentStep) {
      case 1:
        return !!this.formData.category;
      case 2:
        return !!this.formData.severity;
      case 3:
        return !!this.formData.description?.trim();
      case 4:
        return true;
      default:
        return false;
    }
  }

  /**
   * Move to next step
   */
  nextStep(): void {
    if (this.isStepValid() && this.currentStep < 4) {
      this.currentStep++;
    }
  }

  /**
   * Move to previous step
   */
  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  /**
   * Select a category
   */
  selectCategory(category: TicketCategory): void {
    this.formData.category = category;
    // Auto-advance to severity step
    this.nextStep();
  }

  /**
   * Select severity and auto-advance
   */
  selectSeverity(severity: TicketSeverity): void {
    this.formData.severity = severity;
    // Auto-advance to details step
    this.nextStep();
  }

  /**
   * Get category label
   */
  getCategoryLabel(category: TicketCategory): string {
    const labels: Record<TicketCategory, string> = {
      [TicketCategory.BUG]: 'Bug Report',
      [TicketCategory.FEATURE_REQUEST]: 'Feature Request',
      [TicketCategory.ACCOUNT]: 'Account Issue',
      [TicketCategory.BILLING]: 'Billing Question',
      [TicketCategory.PERFORMANCE]: 'Performance Issue',
      [TicketCategory.OTHER]: 'Other',
    };
    return labels[category] || category;
  }

  /**
   * Get category description
   */
  getCategoryDescription(category: TicketCategory): string {
    const descriptions: Record<TicketCategory, string> = {
      [TicketCategory.BUG]: 'Something is not working as expected',
      [TicketCategory.FEATURE_REQUEST]: 'Suggest a new feature or improvement',
      [TicketCategory.ACCOUNT]: 'Account access or settings issue',
      [TicketCategory.BILLING]: 'Questions about billing or pricing',
      [TicketCategory.PERFORMANCE]: 'App is slow or unresponsive',
      [TicketCategory.OTHER]: 'Something else',
    };
    return descriptions[category] || '';
  }

  /**
   * Get category icon
   */
  getCategoryIcon(category: TicketCategory): string {
    const icons: Record<TicketCategory, string> = {
      [TicketCategory.BUG]: 'bug-outline',
      [TicketCategory.FEATURE_REQUEST]: 'star-outline',
      [TicketCategory.ACCOUNT]: 'person-circle-outline',
      [TicketCategory.BILLING]: 'card-outline',
      [TicketCategory.PERFORMANCE]: 'flash-outline',
      [TicketCategory.OTHER]: 'help-circle-outline',
    };
    return icons[category] || 'help-circle-outline';
  }

  /**
   * Get severity label
   */
  getSeverityLabel(severity: TicketSeverity): string {
    const labels: Record<TicketSeverity, string> = {
      [TicketSeverity.CRITICAL]: 'Critical',
      [TicketSeverity.HIGH]: 'High',
      [TicketSeverity.MEDIUM]: 'Medium',
      [TicketSeverity.LOW]: 'Low',
    };
    return labels[severity] || severity;
  }

  /**
   * Get severity icon
   */
  getSeverityIcon(severity: TicketSeverity): string {
    const icons: Record<TicketSeverity, string> = {
      [TicketSeverity.CRITICAL]: 'alert-circle-outline',
      [TicketSeverity.HIGH]: 'warning-outline',
      [TicketSeverity.MEDIUM]: 'information-circle-outline',
      [TicketSeverity.LOW]: 'checkmark-circle-outline',
    };
    return icons[severity] || 'information-circle-outline';
  }

  /**
   * Handle file selection
   */
  onFileSelected(event: any): void {
    const files: FileList = event.target.files;
    const maxFileSize = 10 * 1024 * 1024; // 10MB

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Validate file size
      if (file.size > maxFileSize) {
        this.errorMessage = `File "${file.name}" is too large (max 10MB)`;
        return;
      }

      // Check if file already exists
      if (!this.selectedFiles.find(f => f.name === file.name)) {
        this.selectedFiles.push(file);
      }
    }

    this.errorMessage = ''; // Clear any previous errors
  }

  /**
   * Remove a file from the list
   */
  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Submit the ticket
   */
  submitTicket(): void {
    // Validate required fields
    if (!this.formData.description?.trim()) {
      this.errorMessage = 'Please provide a description of the issue.';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    // Use first line of description as abstract if not provided
    const descriptionLines = this.formData.description.split('\n');
    const abstract = this.formData.abstract || descriptionLines[0].substring(0, 100);

    this.supportService.createTicket(
      abstract,
      this.logId,
      this.formData.description,
      this.formData.category,
      this.formData.severity,
      this.formData.reproducibilitySteps,
      this.formData.expectedBehavior,
      this.formData.actualBehavior,
      this.formData.systemContext
    ).subscribe({
      next: (ticket) => {
        this.successMessage = 'Ticket created successfully!';
        setTimeout(() => {
          this.modalController.dismiss({ ticket });
        }, 1500);
      },
      error: (err) => {
        console.error('Error creating ticket:', err);
        this.errorMessage = 'Failed to create ticket. Please try again later.';
        this.isSubmitting = false;
      },
      complete: () => {
        this.isSubmitting = false;
      },
    });
  }

  closeModal(): void {
    this.modalController.dismiss();
  }
}

